import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import moment from "moment";
import AddButton, { DraftButton } from "../../buttons/button";
import {
  purchaseProductDetailsApi,
  purchaseProductListApi,
} from "../../../redux/apis/purchase-api";
import {
  defaultBinLocationApi,
  searchBinLocationApi,
} from "../../../redux/apis/general-api";
import { warehouseGetIdApi } from "../../../redux/apis/inventory-api";
import { extractPdfTextPages } from "../../../utils/pdf-text-extract";
import {
  parseStockTransferPdf,
  pickBestItemMatch,
  stripEllipsis,
} from "../../../utils/stock-in-pdf-parser";

// Stock IN "Import PDF": reads a Stock Transfer challan, matches every line to
// an item and its RACK to a bin, and shows the result for the user to check
// before anything is put on the transfer grid.
//
// The APIs are called directly rather than through their redux actions: those
// sagas are takeLatest, so firing one lookup per line would cancel all but the
// last.

const LOOKUP_CONCURRENCY = 4;

// Run async jobs a few at a time
const runLimited = async (jobs, limit = LOOKUP_CONCURRENCY) => {
  const results = new Array(jobs.length);
  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const index = next++;
      try {
        results[index] = await jobs[index]();
      } catch (error) {
        results[index] = null;
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, jobs.length) }, worker)
  );
  return results;
};

const searchProducts = async (value) => {
  if (!value) return [];
  // purchaseProductListApi URL-encodes the value itself
  const res = await purchaseProductListApi({
    field: "item_name",
    value,
  });
  return Array.isArray(res?.data?.data) ? res.data.data : [];
};

const fetchProductDetails = async (product) => {
  const res = await purchaseProductDetailsApi({
    item_id: product?.item_id,
    item_uom: product?.item_uom || "",
  });
  return res?.data?.data ?? null;
};

const searchBins = async (warehouseId, value) => {
  if (!value) return [];
  const res = await searchBinLocationApi({
    warehouse_id: warehouseId,
    field: "bin_location_no",
    value: encodeURIComponent(value),
  });
  return Array.isArray(res?.data?.data) ? res.data.data : [];
};

const toBinOption = (bin) =>
  bin?.bin_no_id
    ? { value: bin.bin_no_id, label: bin.bin_location_no ?? "" }
    : null;

const toProductOption = (product) => ({
  value: product?.item_id,
  label: product?.item_name,
  product,
});

// The UOM the row goes in with — same choice the item search makes
// (the item's purchasing UOM, else the first UOM it has)
const resolveUom = (product, details) => {
  const rawUomList = details?.uom_dropdown ?? [];
  const matchedUom =
    rawUomList.find(
      (uom) => uom?.item_id === product?.item_pur_purchasing_uom_name_id
    ) ??
    rawUomList.find(
      (uom) => uom?.alt_uom === product?.item_pur_purchasing_uom_name
    ) ??
    rawUomList[0];
  return { rawUomList, matchedUom };
};

// Search the item master for a challan description: the full name first,
// then its first word, so a slightly different spelling still lists the
// item for the user to pick.
const matchProduct = async (pdfName) => {
  const term = stripEllipsis(pdfName);
  const results = await searchProducts(term);
  let match = pickBestItemMatch(pdfName, results);
  let candidates = results;
  if (!match) {
    const firstWord = term.split(" ")[0];
    if (firstWord && firstWord !== term && firstWord.length >= 3) {
      const wider = await searchProducts(firstWord);
      match = pickBestItemMatch(pdfName, wider);
      if (!results.length) candidates = wider;
    }
  }
  return { match, candidates };
};

const isWhole = (n) => Number.isInteger(Number(n));

// What still stops a line from going onto the grid
const lineIssues = (line) => {
  const issues = [];
  if (!line.product) issues.push("Select item");
  else if (!line.details) issues.push("Item details not loaded");
  if (!line.exp_date) issues.push("Expiry not read");
  if (!(line.qty > 0) || !isWhole(line.qty)) issues.push("Qty must be whole");
  if (line.free && !isWhole(line.free)) issues.push("Free qty must be whole");
  if (!(line.mrp > 0)) issues.push("MRP missing");
  if (!(line.net > 0)) issues.push("Final PR missing");
  return issues;
};

const formatAmount = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toFixed(2);

const nameMatches = (a, b) => {
  const clean = (s) =>
    String(s ?? "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, " ")
      .trim();
  const x = clean(a);
  const y = clean(b);
  return !!x && !!y && (x === y || x.includes(y) || y.includes(x));
};

const chip = {
  ok: "bg-[#ECFDF3] text-[#027A48]",
  warn: "bg-[#FFFAEB] text-[#B54708]",
  error: "bg-[#FEF3F2] text-[#B42318]",
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    fontSize: "13px",
    borderRadius: "6px",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  option: (base) => ({ ...base, fontSize: "13px" }),
  menuPortal: (base) => ({ ...base, zIndex: 1400 }),
};

const StockInPdfImportModal = ({
  file,
  onClose = () => {},
  onConfirm = () => {},
  warehouseList = [],
  selectedSource = null,
  destinationIds = [],
}) => {
  const [phase, setPhase] = useState("reading"); // reading | resolving | ready | error
  const [errorMessage, setErrorMessage] = useState("");
  const [meta, setMeta] = useState({});
  const [lines, setLines] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [source, setSource] = useState(selectedSource);
  const [confirming, setConfirming] = useState(false);
  const cancelledRef = useRef(false);

  const sourceOptions = useMemo(
    () =>
      warehouseList.filter((opt) => !destinationIds.includes(opt?.value)),
    [warehouseList, destinationIds]
  );

  const updateLine = (key, patch) =>
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );

  useEffect(() => {
    cancelledRef.current = false;
    const run = async () => {
      let parsed;
      try {
        const pages = await extractPdfTextPages(file);
        parsed = parseStockTransferPdf(pages);
      } catch (error) {
        if (cancelledRef.current) return;
        setErrorMessage("Could not read this PDF. Please check the file.");
        setPhase("error");
        return;
      }
      if (cancelledRef.current) return;
      if (!parsed.lines.length) {
        setErrorMessage(
          "No item lines were found in this PDF. Only Stock Transfer PDFs with DESCRIPTION, BATCH NO and QTY columns can be imported."
        );
        setPhase("error");
        return;
      }

      setMeta(parsed.meta);
      if (!selectedSource && parsed.meta?.from_name) {
        const guess = sourceOptions.filter((opt) =>
          nameMatches(opt?.label, parsed.meta.from_name)
        );
        if (guess.length === 1) setSource(guess[0]);
      }
      const initialLines = parsed.lines.map((line, index) => ({
        ...line,
        key: index,
        include: true,
        matchState: "searching",
        binState: "searching",
        product: null,
        details: null,
        candidates: [],
        bin: null,
      }));
      setLines(initialLines);
      setPhase("resolving");

      // Store this stock is coming into, and its default receiving bin
      let currentWarehouseId = "";
      let defaultBin = null;
      try {
        const idRes = await warehouseGetIdApi();
        currentWarehouseId =
          idRes?.data?.data?.warehouse_id ?? idRes?.data?.data?.pos_id ?? "";
        const binRes = await defaultBinLocationApi({
          warehouse_id: currentWarehouseId,
          field: "default_dropping_bin",
        });
        defaultBin = toBinOption(binRes?.data?.data);
      } catch (error) {}
      if (cancelledRef.current) return;
      setWarehouseId(currentWarehouseId);

      // One lookup per distinct description / rack, not per line
      const names = [...new Set(initialLines.map((line) => line.name))];
      const racks = [
        ...new Set(initialLines.map((line) => line.rack).filter(Boolean)),
      ];

      const [productResults, binResults] = await Promise.all([
        runLimited(
          names.map((name) => async () => {
            const { match, candidates } = await matchProduct(name);
            const details = match
              ? await fetchProductDetails(match.item)
              : null;
            return { name, match, details, candidates };
          })
        ),
        runLimited(
          racks.map((rack) => async () => {
            const bins = await searchBins(currentWarehouseId, rack);
            const exact = bins.find(
              (bin) =>
                String(bin?.bin_location_no ?? "").trim().toUpperCase() ===
                rack.toUpperCase()
            );
            return { rack, bin: toBinOption(exact) };
          })
        ),
      ]);
      if (cancelledRef.current) return;

      const productByName = {};
      productResults.forEach((result, index) => {
        productByName[names[index]] = result;
      });
      const binByRack = {};
      binResults.forEach((result, index) => {
        binByRack[racks[index]] = result?.bin ?? null;
      });

      setLines((prev) =>
        prev.map((line) => {
          const found = productByName[line.name];
          const rackBin = line.rack ? binByRack[line.rack] : null;
          const product = found?.match?.item ?? null;
          return {
            ...line,
            product,
            details: found?.details ?? null,
            candidates: found?.candidates ?? [],
            matchState: !product
              ? "missing"
              : !found?.details
                ? "missing"
                : found?.match?.exact
                  ? "matched"
                  : "check",
            bin: rackBin ?? defaultBin,
            binState: rackBin ? "matched" : defaultBin ? "default" : "missing",
          };
        })
      );
      setPhase("ready");
    };
    run();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // Close on Escape (an open item/bin menu takes the first Escape)
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadProductOptions = useMemo(
    () =>
      debounce((input, callback) => {
        searchProducts(input)
          .then((products) => callback(products.map(toProductOption)))
          .catch(() => callback([]));
      }, 400),
    []
  );

  const loadBinOptions = useMemo(
    () =>
      debounce((input, callback) => {
        searchBins(warehouseId, input)
          .then((bins) => callback(bins.map(toBinOption).filter(Boolean)))
          .catch(() => callback([]));
      }, 400),
    [warehouseId]
  );

  // Picking the item for one line also fills every other unmatched line
  // with the same description (the same product in another batch).
  const handlePickProduct = async (line, option) => {
    if (!option?.product) return;
    const product = option.product;
    const sameName = (other) =>
      other.key === line.key ||
      (other.name === line.name && other.matchState !== "matched");
    setLines((prev) =>
      prev.map((other) =>
        sameName(other)
          ? { ...other, product, details: null, matchState: "searching" }
          : other
      )
    );
    let details = null;
    try {
      details = await fetchProductDetails(product);
    } catch (error) {}
    setLines((prev) =>
      prev.map((other) =>
        sameName(other) && other.product?.item_id === product.item_id
          ? {
              ...other,
              details,
              matchState: details ? "matched" : "missing",
            }
          : other
      )
    );
  };

  const included = lines.filter((line) => line.include);
  const blocked = included.filter((line) => lineIssues(line).length > 0);
  const totals = included.reduce(
    (acc, line) => ({
      qty: acc.qty + (Number(line.qty) || 0),
      amt: acc.amt + (Number(line.amt) || 0),
    }),
    { qty: 0, amt: 0 }
  );
  const allTotals = lines.reduce(
    (acc, line) => ({
      qty: acc.qty + (Number(line.qty) || 0),
      amt: acc.amt + (Number(line.amt) || 0),
    }),
    { qty: 0, amt: 0 }
  );
  const countOk =
    meta?.items_count === null ||
    meta?.items_count === undefined ||
    meta.items_count === lines.length;
  const qtyOk =
    meta?.total_qty === null ||
    meta?.total_qty === undefined ||
    meta.total_qty === allTotals.qty;
  // The challan rounds its net amount to the rupee
  const amtOk =
    meta?.net_amount === null ||
    meta?.net_amount === undefined ||
    Math.abs(meta.net_amount - allTotals.amt) <= 1;

  const canConfirm =
    phase === "ready" && included.length > 0 && blocked.length === 0 && !!source;

  const handleConfirm = () => {
    if (!canConfirm) return;
    setConfirming(true);
    // One grid row per item, one batch per challan line
    const groups = [];
    included.forEach((line) => {
      const group = groups.find(
        (g) => g.product.item_id === line.product.item_id
      );
      if (group) group.lines.push(line);
      else
        groups.push({
          product: line.product,
          details: line.details,
          lines: [line],
        });
    });
    const rows = groups.map(({ product, details, lines: groupLines }) => {
      const { rawUomList, matchedUom } = resolveUom(product, details);
      const baseQuantity = parseFloat(matchedUom?.base_quantity) || 1;
      const rawSellableQuantity =
        parseFloat(details?.total_sellable_quantity) || 0;
      const item_batches = groupLines.map((line) => ({
        item_id: product.item_id,
        item_batch_number: line.batch,
        remainingQty: 0,
        item_batch_quantity: Number(line.qty),
        from_bin_location: "",
        to_bin_location: line.bin?.label ?? "",
        to_bin_id: line.bin?.value ?? "",
        from_bin_id: "",
        created_date: "",
        item_exp_date: line.exp_date,
        item_batch_free_quantity: Number(line.free) || 0,
        // Final PR is the challan's NET (rate after discount, with GST)
        item_batch_purchase_rate: Number(line.net),
        item_batch_unit_price: Number(line.mrp),
        item_batch_discount_percentage: Number(line.dis) || 0,
        item_batch_discount_amount: Number(line.dis_amount) || 0,
      }));
      return {
        sl_no: 1,
        item_name: product.item_name,
        item_code: product.item_code,
        item_uom:
          product.item_pur_purchasing_uom_name || matchedUom?.alt_uom || "",
        quantity: item_batches.reduce(
          (sum, batch) => sum + (Number(batch.item_batch_quantity) || 0),
          0
        ),
        item_batches,
        current_avail: {
          item_uom_source: "",
          item_uom_destination: "",
        },
        total_sellable_quantity: rawSellableQuantity / baseQuantity,
        base_total_sellable_quantity: rawSellableQuantity,
        item_id: product.item_id,
        uom_dropdown: rawUomList.map((uom) => ({
          item_uom_id: uom?.item_id,
          label: uom?.alt_uom,
          value: uom?.alt_uom,
          base_quantity: uom?.base_quantity,
        })),
        item_uom_id:
          matchedUom?.item_id ?? product.item_pur_purchasing_uom_name_id ?? "",
        base_quantity: baseQuantity,
        uom_group_id: product.item_uom || "",
      };
    });
    onConfirm({ rows, source, meta, lineCount: included.length });
  };

  const statusChip = (state, labels) => (
    <span
      className={`inline-block px-2 py-[2px] rounded-full text-[11px] font-medium whitespace-nowrap ${
        state === "matched"
          ? chip.ok
          : state === "searching"
            ? "bg-[#F2F4F7] text-[#475467]"
            : state === "missing"
              ? chip.error
              : chip.warn
      }`}
    >
      {labels[state]}
    </span>
  );

  const th =
    "px-2 py-2 text-left text-[12px] font-semibold text-[var(--create-table-header-fg)] whitespace-nowrap bg-[var(--create-table-header-bg)] sticky top-0 z-[1]";
  const td =
    "px-2 py-2 text-[13px] text-[var(--create-table-row-fg-color)] border-b border-[var(--create-table-border)] align-top";
  const num = `${td} text-right whitespace-nowrap`;

  const summaryItem = (label, value) => (
    <div className="flex flex-col min-w-0">
      <span className="text-[11px] uppercase tracking-wide text-[#667085]">
        {label}
      </span>
      <span className="text-[14px] font-medium text-[#101828] truncate">
        {value || "-"}
      </span>
    </div>
  );

  const checkBadge = (ok, text) => (
    <span
      className={`px-2 py-1 rounded-md text-[12px] font-medium whitespace-nowrap ${
        ok ? chip.ok : chip.warn
      }`}
    >
      {ok ? "✓" : "!"} {text}
    </span>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-[rgba(0,0,0,0.35)] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[1400px] h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-[60px] border-b border-[var(--slide-panel-border,#EAECF0)] bg-[var(--slide-panel-head-bg)]">
          <div className="min-w-0">
            <div className="text-[16px] font-semibold text-[var(--slide-panel-h-fg,#101828)]">
              Import Stock IN from PDF
            </div>
            <div className="text-[12px] text-[#667085] truncate">
              {file?.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-[32px] px-3 rounded-md bg-[var(--close-button-bg)] text-[var(--close-button-fg)] text-[13px]"
          >
            Close <span className="opacity-50">(Esc)</span>
          </button>
        </div>

        {phase === "reading" || phase === "error" ? (
          <div className="flex-1 flex items-center justify-center px-6">
            {phase === "reading" ? (
              <div className="text-[14px] text-[#475467]">Reading PDF…</div>
            ) : (
              <div className="max-w-[520px] text-center text-[14px] text-[#B42318]">
                {errorMessage}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Document summary */}
            <div className="px-5 py-3 border-b border-[#EAECF0] flex flex-wrap items-end gap-x-8 gap-y-3">
              {summaryItem("Doc No", meta?.doc_no)}
              {summaryItem(
                "Doc Date",
                meta?.doc_date
                  ? moment(meta.doc_date).format("DD-MMM-YYYY")
                  : ""
              )}
              {summaryItem("From (PDF)", meta?.from_name)}
              {summaryItem("To (PDF)", meta?.to_name)}
              <div className="flex flex-col w-[260px]">
                <span className="text-[11px] uppercase tracking-wide text-[#667085]">
                  Source (from) <span className="text-[#B42318]">*</span>
                </span>
                <Select
                  value={source}
                  onChange={setSource}
                  options={sourceOptions}
                  placeholder="Select source store"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isSearchable
                />
              </div>
              <div className="flex flex-wrap gap-2 ml-auto">
                {checkBadge(
                  countOk,
                  `Lines ${lines.length}${
                    meta?.items_count ? ` / ${meta.items_count} in PDF` : ""
                  }`
                )}
                {checkBadge(
                  qtyOk,
                  `Qty ${allTotals.qty}${
                    meta?.total_qty ? ` / ${meta.total_qty} in PDF` : ""
                  }`
                )}
                {checkBadge(
                  amtOk,
                  `Amount ${formatAmount(allTotals.amt)}${
                    meta?.net_amount !== null && meta?.net_amount !== undefined
                      ? ` / ${formatAmount(meta.net_amount)} in PDF`
                      : ""
                  }`
                )}
              </div>
            </div>

            {/* Lines */}
            <div className="flex-1 overflow-auto px-5 pb-3">
              <table className="w-full border-collapse mt-3">
                <thead>
                  <tr>
                    <th className={th}>
                      <input
                        type="checkbox"
                        checked={lines.length > 0 && lines.every((l) => l.include)}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((line) => ({
                              ...line,
                              include: e.target.checked,
                            }))
                          )
                        }
                      />
                    </th>
                    <th className={th}>#</th>
                    <th className={th}>PDF Description</th>
                    <th className={`${th} min-w-[260px]`}>Item</th>
                    <th className={th}>UOM</th>
                    <th className={th}>Batch</th>
                    <th className={th}>Expiry</th>
                    <th className={`${th} text-right`}>Qty</th>
                    <th className={`${th} text-right`}>Free</th>
                    <th className={`${th} text-right`}>MRP</th>
                    <th className={`${th} text-right`}>Rate</th>
                    <th className={`${th} text-right`}>Disc %</th>
                    <th className={`${th} text-right`}>Disc Amt</th>
                    <th className={`${th} text-right`}>GST %</th>
                    <th className={`${th} text-right`}>Final PR</th>
                    <th className={`${th} text-right`}>Amount</th>
                    <th className={`${th} min-w-[190px]`}>Rack → To Bin</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => {
                    const issues = line.include ? lineIssues(line) : [];
                    const uom = line.product
                      ? resolveUom(line.product, line.details)
                      : null;
                    return (
                      <tr
                        key={line.key}
                        className={
                          line.include
                            ? issues.length && phase === "ready"
                              ? "bg-[#FFFBFA]"
                              : ""
                            : "opacity-40"
                        }
                      >
                        <td className={td}>
                          <input
                            type="checkbox"
                            checked={line.include}
                            onChange={(e) =>
                              updateLine(line.key, {
                                include: e.target.checked,
                              })
                            }
                          />
                        </td>
                        <td className={td}>{line.sno ?? index + 1}</td>
                        <td className={`${td} whitespace-nowrap`}>
                          {line.name}
                          {line.pack && (
                            <div className="text-[11px] text-[#667085]">
                              Pack {line.pack}
                            </div>
                          )}
                        </td>
                        <td className={td}>
                          <div className="flex flex-col gap-1">
                            <AsyncSelect
                              value={
                                line.product
                                  ? toProductOption(line.product)
                                  : null
                              }
                              onChange={(option) =>
                                handlePickProduct(line, option)
                              }
                              loadOptions={loadProductOptions}
                              defaultOptions={line.candidates.map(
                                toProductOption
                              )}
                              placeholder="Search item…"
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              isDisabled={
                                !line.include || phase !== "ready"
                              }
                              noOptionsMessage={() => "Type to search items"}
                            />
                            <div className="flex flex-wrap gap-1">
                              {statusChip(line.matchState, {
                                searching: "Matching…",
                                matched: "Matched",
                                check: "Please verify",
                                missing: "Select item",
                              })}
                              {issues
                                .filter(
                                  (issue) =>
                                    issue !== "Select item" &&
                                    phase === "ready"
                                )
                                .map((issue) => (
                                  <span
                                    key={issue}
                                    className={`inline-block px-2 py-[2px] rounded-full text-[11px] font-medium ${chip.error}`}
                                  >
                                    {issue}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </td>
                        <td className={`${td} whitespace-nowrap`}>
                          {uom
                            ? line.product?.item_pur_purchasing_uom_name ||
                              uom.matchedUom?.alt_uom ||
                              "-"
                            : "-"}
                        </td>
                        <td className={`${td} whitespace-nowrap`}>
                          {line.batch}
                        </td>
                        <td className={`${td} whitespace-nowrap`}>
                          {line.exp_date
                            ? moment(line.exp_date).format("MMM-YYYY")
                            : line.exp_raw || "-"}
                        </td>
                        <td className={num}>{line.qty}</td>
                        <td className={num}>{line.free || 0}</td>
                        <td className={num}>{formatAmount(line.mrp)}</td>
                        <td className={num}>{formatAmount(line.rate)}</td>
                        <td className={num}>{line.dis || 0}</td>
                        <td className={num}>{formatAmount(line.dis_amount)}</td>
                        <td className={num}>{line.gst || 0}</td>
                        <td className={`${num} font-semibold`}>
                          {formatAmount(line.net)}
                        </td>
                        <td className={num}>{formatAmount(line.amt)}</td>
                        <td className={td}>
                          <div className="flex flex-col gap-1">
                            <AsyncSelect
                              value={line.bin}
                              onChange={(option) =>
                                updateLine(line.key, {
                                  bin: option,
                                  binState: option ? "matched" : "missing",
                                })
                              }
                              loadOptions={loadBinOptions}
                              defaultOptions={false}
                              placeholder="Search bin…"
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              isDisabled={
                                !line.include || phase !== "ready"
                              }
                              noOptionsMessage={() => "Type to search bins"}
                            />
                            <div className="flex items-center gap-1">
                              <span className="text-[11px] text-[#667085]">
                                {line.rack || "No rack"}
                              </span>
                              {statusChip(line.binState, {
                                searching: "Matching…",
                                matched: "Bin found",
                                default: "Rack not found · default bin",
                                missing: "No bin",
                              })}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="h-[64px] px-5 flex items-center justify-between border-t border-[#EAECF0]">
          <div className="text-[13px] text-[#475467]">
            {phase === "resolving" && "Matching items and bins…"}
            {phase === "ready" && (
              <>
                {included.length} of {lines.length} lines selected · Qty{" "}
                {totals.qty} · Amount {formatAmount(totals.amt)}
                {blocked.length > 0 && (
                  <span className="text-[#B42318]">
                    {" "}
                    · {blocked.length} line
                    {blocked.length > 1 ? "s" : ""} need attention (fix or
                    untick)
                  </span>
                )}
                {!source && (
                  <span className="text-[#B42318]"> · Select the source store</span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-3">
            <DraftButton
              text="Cancel"
              onclkFn={onClose}
              className="px-6 h-[40px] rounded-md border border-[var(--primary-button-bg-color)] text-[var(--primary-button-bg-color)] text-[14px] font-medium"
            />
            <AddButton
              prefixIcon={false}
              text={`Confirm & Add ${included.length || ""} Line${
                included.length === 1 ? "" : "s"
              }`}
              onclkFn={handleConfirm}
              isDisabled={!canConfirm || confirming}
              className={`flex items-center px-[24px] h-[40px] whitespace-nowrap rounded-md bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] text-[14px] font-medium ${
                !canConfirm ? "opacity-50" : "hover:bg-[var(--primary-button-hover-color)]"
              }`}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StockInPdfImportModal;
